import { useRef, Ref, forwardRef, useImperativeHandle } from 'react'
import { InputRef, LegacyInput, touchInputs } from './input'
import { stubStorage } from '@/utils/env'
import { ProductGroup, ProductGroupBody } from 'shared/types/api'
import { useVenueContext } from '@/services/venue'
import { useAppContext } from '@/pages/_app'

export type ProductGroupFormProps = {
  sourceProductGroup?: ProductGroup
}

export type ProductGroupFormRef = {
  process(): Promise<ProductGroupFormResult>
}

export type ProductGroupFormResult =
  | {
      type: 'ok'
      body: ProductGroupBody
    }
  | {
      type: 'validationError'
    }

export const ProductGroupForm = forwardRef(ProductGroupFormWithRef)

export function ProductGroupFormWithRef(
  props: ProductGroupFormProps,
  ref: Ref<ProductGroupFormRef>,
) {
  const { t } = useAppContext()
  const { venueName } = useVenueContext()!

  const refs = {
    title: useRef<InputRef>(null),
    descriptionShort: useRef<InputRef>(null),
  }

  const productGroupPrototype: ProductGroup = {
    kind: 'internal',
    name: '',
    title: '',
    descriptionShort: '',
    products: [],
    features: {},
  }

  const sourceProductGroup = props.sourceProductGroup ?? productGroupPrototype

  useImperativeHandle(ref, () => {
    return {
      async process() {
        const isValid = touchInputs(Object.values(refs)).isValid
        if (!isValid) {
          return {
            type: 'validationError',
          }
        }

        const body: ProductGroupBody = {
          title: refs.title.current!.value.trim(),
          descriptionShort: refs.descriptionShort.current!.value.trim(),
        }

        return {
          type: 'ok',
          body,
        }
      },
    }
  })

  const productGroupKey = props.sourceProductGroup
    ? `${venueName}/${props.sourceProductGroup.name}`
    : `${venueName}/new`

  return (
    <>
      <div className="w-full text-opaque dark:text-opaqueDark"></div>
      <div className="mt-4">
        <LegacyInput
          autoFocus={props.sourceProductGroup === undefined}
          ref={refs.title}
          id={`product/${productGroupKey}/title`}
          label={t('productGroupForm:groupName')}
          required
          type="text"
          value={sourceProductGroup.title}
          storage={stubStorage}
          height={2}
        />
      </div>
      <div className="mt-4">
        <LegacyInput
          ref={refs.descriptionShort}
          id={`product/${productGroupKey}/descriptionShort`}
          label={t('common:shortDescription')}
          type="text"
          value={sourceProductGroup.descriptionShort || ''}
          storage={stubStorage}
          height={3}
        />
      </div>
    </>
  )
}
